/**
 * Right to erasure, on the profile page — `DELETE /api/account` (PIVOT-063,
 * constitution §V). The endpoint has existed and been tested since PIVOT-063;
 * this is the first thing that calls it, and PRIVACY.md has been promising
 * users a "Delete my account" button the whole time.
 *
 * Two-step on purpose. The first button only opens the panel; erasing needs the
 * checkbox ticked as well, because there is no undo on the other side — no soft
 * delete, no grace period, and the CV blob goes with the rows. A single
 * misplaced click must not be able to do that.
 *
 * Owns its own endpoint end to end (CLAUDE.md §2): nothing else on the page
 * needs the result, because on success there is no page left to update.
 */

import { useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { deleteAccount } from '../api/account'
import { useAuth } from '../auth/AuthContext'
import { useI18n } from '../i18n'
import { Icon } from './Icon'
import './DeleteAccount.css'

export function DeleteAccount() {
  const { t } = useI18n()
  const { signOut } = useAuth()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [understood, setUnderstood] = useState(false)
  const [deleting, setDeleting] = useState(false)
  // A flag, not a message (frontend/CLAUDE.md §7): `failed` translates at
  // render, so it survives a locale change without a refetch. `DownloadData`
  // and `SignOutEverywhere` beside it already do this right; this used to
  // store the translated string instead (PIVOT-145).
  const [error, setError] = useState(false)

  const handleDelete = async () => {
    if (deleting || !understood) return
    setDeleting(true)
    setError(false)
    try {
      await deleteAccount()
    } catch {
      // The account is untouched on any failure — the backend erases in one
      // committed transaction — so say so and leave the panel open to retry.
      setError(true)
      setDeleting(false)
      return
    }
    // The token still parses but names a user who no longer exists, so drop it
    // here rather than letting the next request 401 into a generic error.
    signOut()
    void navigate('/', { replace: true })
  }

  return (
    <section className="delete-account">
      <h3>{t('profile.deleteTitle')}</h3>
      <p className="delete-account-body">
        <Icon name="alert" className="delete-account-alert" />
        {t('profile.deleteBody')}
      </p>
      {/* The only route back to the policy once you are signed in. Its erasure
          section says the same thing in full, which is worth reading before
          pressing a button with no undo. */}
      <Link to="/privacy" className="delete-account-policy">
        {t('profile.privacyLink')}
      </Link>
      {error && <p className="form-error">{t('profile.deleteFailed')}</p>}

      {open ? (
        <>
          <label className="delete-account-confirm">
            <input
              type="checkbox"
              checked={understood}
              onChange={(e) => setUnderstood(e.target.checked)}
              disabled={deleting}
            />
            {t('profile.deleteConfirmLabel')}
          </label>
          <div className="delete-account-actions">
            <button
              type="button"
              className="btn delete-account-go"
              disabled={!understood || deleting}
              onClick={() => void handleDelete()}
            >
              {deleting ? t('common.loading') : t('profile.deleteConfirm')}
            </button>
            <button
              type="button"
              className="btn btn-outline"
              disabled={deleting}
              onClick={() => {
                setOpen(false)
                setUnderstood(false)
                setError(false)
              }}
            >
              {t('common.cancel')}
            </button>
          </div>
        </>
      ) : (
        <button type="button" className="btn btn-outline" onClick={() => setOpen(true)}>
          {t('profile.deleteStart')}
        </button>
      )}
    </section>
  )
}
